import {inject, Injectable, NgZone} from "@angular/core";
import { Constants } from "../models/constants";
import { UserAccount } from "../models/custom-models/user-account";
import { LoginService } from "./login.service";
import jwt_decode from "jwt-decode";
import { NgxSpinnerService } from "ngx-spinner";
import { AccessDomain } from "../models/access-domain";
import { AllTeamsOfOrgResponse } from "../models/team";
import { Country } from "../models/user";
import { OrgBUProjectTeamStructure } from "../models/custom-models/org-buproject-team-structure";
import { GloabllySelectedEntity } from "../models/custom-models/entity-info";
import { ToastrService } from "ngx-toastr";
import {SecureStorageService} from "../modules/core/services/secure-storage.service";
import {LocalStorageRelatedData} from "../local-storage-related-data.model";
import {UserOptionSelectComponent} from "../modules/chat-commands/components/user-option-select/user-option-select.component";
import * as fuzzysort from "fuzzysort";
import {firstValueFrom} from "rxjs";
import {MatDialog} from "@angular/material/dialog";
import {UserDetails, UserOption} from "../models/userDetails";
import {UserExtendedLabelOptionSelectComponent} from "../modules/chat-commands/components/user-extended-label-option-select/user-extended-label-option-select.component";
import {DropDownOption} from "../modules/shared/Models/shared-data-models.model";

@Injectable({
    providedIn: "root",
})
export class SharedService {
    showImmediateAttentionButtonDefaultColour: boolean = true;
    isFCMTokenValidated: boolean = false;
    MILLISECONDS_IN_DAY = 24 * 60 * 60 * 1000;
    private BACKUP_ORG_ADMIN_ROLE_ID: number = Constants.Roles.BACKUP_ORG_ADMIN_ROLE_ID;
    private ORG_ADMIN_ROLE_ID: number = Constants.Roles.ORG_ADMIN_ROLE_ID;
    private selectedTaskIdForImmediateAttention: number;
    private receivedWebSocketServerMessage: any[] = [];
    private isUserReceivedImeediateAttention: boolean = false;
    private secureStorageService = inject(SecureStorageService)

    private toastr = inject(ToastrService)
    private dialog = inject(MatDialog)

    constructor(
        private loginService: LoginService,
        private spinner: NgxSpinnerService,
        private ngZone: NgZone,
    ) {}

    public getSelectedTaskIdForImmediateAttention(): number {
        return this.selectedTaskIdForImmediateAttention;
    }

    public setSelectedTaskIdForImmediateAttention(taskId: number) {
        this.selectedTaskIdForImmediateAttention = taskId;
    }

    setIsUserReceivedImmediateAttention(isUserReceivedImmediateAttention: boolean) {
        this.isUserReceivedImeediateAttention = isUserReceivedImmediateAttention;
    }

    checkIsUserReceivedImmediateAttention(): boolean {
        return this.isUserReceivedImeediateAttention;
    }

    resetIsUserReceivedImmediateAttention() {
        this.isUserReceivedImeediateAttention = false;
    }

    public getWebSocketServerMessage(): any {
        return this.receivedWebSocketServerMessage;
    }

    public setWebSocketServerMessage(message: any) {
        this.receivedWebSocketServerMessage.push(message);
        this.checkWebSocketMessageByServer();
    }

    public checkWebSocketMessageByServer() {
        this.showImmediateAttentionButtonDefaultColour = this.receivedWebSocketServerMessage.length == 0;
    }

    public resetAllWebSocketMessageByServer() {
        this.receivedWebSocketServerMessage.length = 0;
        this.checkWebSocketMessageByServer();
    }

    public setUserAccounts(allUserAccounts: UserAccount[]) {
        this.secureStorageService.setItem(LocalStorageRelatedData.LOGGED_IN_USER_ACCOUNTS, allUserAccounts)
    }

    public getAllTeamIdsOfLoggedInUser(): number[] {
        return this.findAllTeamIdOfLoggedInUser();
    }

    public getAllOrgIdsOfLoggedInUser(): number[] {
        let allUserAccounts: UserAccount[] = this.getUserAccounts();
        let allOrgIds: number[] = [];
        if (allUserAccounts && allUserAccounts.length > 0) {
            allUserAccounts.forEach((account: UserAccount) => {
                allOrgIds.push(account.orgId);
            });
            return allOrgIds;
        } else {
            return [];
        }
    }

    public getUserAccounts(): UserAccount[] {
        return this.secureStorageService.getItem<UserAccount[]>(LocalStorageRelatedData.LOGGED_IN_USER_ACCOUNTS) || [];
    }

    public getLoggedInUserToken(): string {
        return this.secureStorageService.getItem<string>(LocalStorageRelatedData.TOKEN) || "";
    }

    public getLoggedInUserFirstName() {
        return this.secureStorageService.getItem<string>(LocalStorageRelatedData.FIRST_NAME) || '';
    }

    public getLoggedInUserLastName() {
        return this.secureStorageService.getItem<string>(LocalStorageRelatedData.LAST_NAME) || '';
    }

    public getLoggedInUserName() {
        return this.secureStorageService.getItem<string>(LocalStorageRelatedData.USER_NAME) || '';
    }

    public storeAccessDomains(accessDomains: AccessDomain[]) {
        this.secureStorageService.setItem(LocalStorageRelatedData.LOGGED_IN_USER_ACCESS_DOMAINS, accessDomains)
    }

    public getLoggedInUserAccessDomains() {
        return this.secureStorageService.getItem<AccessDomain[]>(LocalStorageRelatedData.LOGGED_IN_USER_ACCESS_DOMAINS) || [];
    }

    public getLoggedInUserPrimaryEmail(): string {
        return this.secureStorageService.getItem(LocalStorageRelatedData.USER_NAME) || '';
    }

    public getLoggedInUserId(): number {
        let loggedInUserId = this.secureStorageService.getItem<number|string>(LocalStorageRelatedData.LOGGED_IN_USER_ID);
        if (loggedInUserId){
            return typeof(loggedInUserId) == 'string' ? parseInt(loggedInUserId) : loggedInUserId;
        } else {
            return 0;
        }
    }

    public getDeviceId() {
        return this.secureStorageService.getItem<string>(LocalStorageRelatedData.DEVICE_ID);
    }

    public getUserFullName() {
        const firstName = this.secureStorageService.getItem<string>(LocalStorageRelatedData.FIRST_NAME) || '';
        const lastName = this.secureStorageService.getItem<string>(LocalStorageRelatedData.LAST_NAME) || '';
        return `${firstName} ${lastName}`;
    }

    public getDecodedJwtToken(): any {
        try {
            return jwt_decode(this.getLoggedInUserToken());
        } catch (Error) {
            return null;
        }
    }

    // --------------Method to find all TeamId of logged in User.

    public findAllTeamIdOfLoggedInUser() {
        let arrays: any[] = this.getLoggedInUserAccessDomains();
        let allFoundTeamIds: number[] = [];
        for (let i of arrays) {
            if (i.entityTypeId == Constants.EntityTypes.TEAM) {
                let foundTeamId = i.entityId;
                allFoundTeamIds.push(foundTeamId);
            }
        }
        if (allFoundTeamIds != null) {
            return allFoundTeamIds;
        } else {
            allFoundTeamIds = [];
            return allFoundTeamIds;
        }
    }

    // ---------- Method to get all selectable Org-----------------
    public getSelectableOrgsList(nodes: OrgBUProjectTeamStructure[]) {
        let result: GloabllySelectedEntity[] = [];

        for (const node of nodes) {
            if (node.entityTypeId === Constants.EntityTypes.ORG && node.isSelectable && node.orgId !== undefined) {
                result.push({
                    entityId: node.orgId,
                    entityName: node.name,
                    entityType: Constants.EntityTypesName.ORGANIZATION,
                    entityTypeId: Constants.EntityTypes.ORG,
                    orgId: node.orgId,
                });
            }
        }
        return result;
    }

    // ---------- Method to get all selectable Project-----------------
    public getSelectableProjectsList(nodes: OrgBUProjectTeamStructure[]) {
        let result: GloabllySelectedEntity[] = [];

        for (const node1 of nodes) {
            if (node1.children) {
                for (const node of node1.children) {
                    if (
                        node.entityTypeId === Constants.EntityTypes.PROJECT &&
                        node.isSelectable &&
                        node.projectId !== undefined
                    ) {
                        result.push({
                            entityId: node.projectId,
                            entityName: node.name,
                            entityType: Constants.EntityTypesName.PROJECT,
                            entityTypeId: Constants.EntityTypes.PROJECT,
                            orgId: node.orgId,
                        });
                    }
                }
            }
        }

        return result;
    }

    // ---------- Method to get all selectable teams-----------------
    public getSelectableTeamsList(nodes: OrgBUProjectTeamStructure[]) {
        let result: GloabllySelectedEntity[] = [];

        for (const node of nodes) {
            if (node.entityTypeId === Constants.EntityTypes.TEAM && node.isSelectable && node.teamId !== undefined) {
                result.push({
                    entityId: node.teamId,
                    entityName: node.name,
                    entityType: Constants.EntityTypesName.TEAM,
                    entityTypeId: Constants.EntityTypes.TEAM,
                    orgId: node.orgId,
                });
            }

            if (node.children && node.children.length > 0) {
                result = result.concat(this.getSelectableTeamsList(node.children));
            }
        }

        return result;
    }

    /** Method to find that is user token is Expired or not? */
    isUserTokenExpired(): boolean {
        let token = this.getLoggedInUserToken();
        // Check if the token is null or empty
        if (!token) {
            console.log("No token found");
            return true; // Token is considered expired if it doesn't exist
        }
        const decodedUserTokenExpiry = this.loginService.getDecodedAccessToken(token).exp * 1000;
        const currentTime = new Date().getTime();
        const timeLeftBeforeExpiry = decodedUserTokenExpiry - currentTime;

        // Check if the token has already expired
        if (timeLeftBeforeExpiry <= 0) {
            this.toastr.warning(Constants.ErrorMessages.SESSION_EXPIRED);
            setTimeout(() => {
                this.ngZone.run(() => {
                    this.loginService.logoutTheUserAndRedirectToLogin();
                });
            }, 2500);
            return true;
        }

        // Check if the token will expire within the next 24 hours
        if (timeLeftBeforeExpiry <= this.MILLISECONDS_IN_DAY) {
            this.toastr.warning(Constants.ErrorMessages.SESSION_EXPIRED);
            setTimeout(() => {
                this.ngZone.run(() => {
                    this.loginService.logoutTheUserAndRedirectToLogin();
                });
            }, 2500);
            return true;
        }

        // Token is valid
        return false;
    }

    getUserAccountIdWithOrgId(orgId: number) {
        let allUserAccounts: any[] = this.getUserAccounts();
        let foundAccountId: number | null = null;
        if (allUserAccounts) {
            for (let i of allUserAccounts) {
                if (i.orgId == orgId) {
                    foundAccountId = i.accountId;
                }
            }
            return foundAccountId;
        } else {
            return foundAccountId;
        }
    }

    getUserAccountIdWithTeamId(teamId: number) {
        let allAccessDomains: AccessDomain[] = this.getLoggedInUserAccessDomains();
        let foundAccountId: number | null = null;
        if (allAccessDomains) {
            for (let i of allAccessDomains) {
                if (i.entityTypeId === Constants.EntityTypes.TEAM && i.entityId == teamId) {
                    foundAccountId = i.accountId;
                }
            }
        }
        return foundAccountId;
    }

    getAccountHeaderIdByOrg(orgId: string): string {
        let loggedInUserAccounts: any[] = this.getUserAccounts();
        let accountHeaderIds: number[] = [];
        loggedInUserAccounts.forEach((account: any) => {
            if (account.orgId == orgId) {
                accountHeaderIds.push(account.accountId);
            }
        });
        return accountHeaderIds.toString();
    }

    getAllAccountIdsOfLoggedInUser(toString: boolean = true): string | number[] {
        let loggedInUserAccounts: any[] = this.getUserAccounts();
        let accountIds: number[] = [];
        if (loggedInUserAccounts && loggedInUserAccounts.length > 0) {
            loggedInUserAccounts?.forEach((account: any) => {
                accountIds.push(account.accountId);
            });
            return toString ? accountIds.toString() : accountIds;
        } else {
            return toString ? "" : [];
        }
    }

    showSpinner() {
        this.spinner.show();
    }

    hideSpinner() {
        this.spinner.hide();
    }

    isUserAdminOfOrg(orgId: number): boolean {
        let isUserAdminOfOrg: boolean = false;
        let userAccessDomains = this.getLoggedInUserAccessDomains() as AccessDomain[];
        if (!userAccessDomains || userAccessDomains.length == 0) {
            return false;
        }
        userAccessDomains?.forEach((accessDomain: AccessDomain) => {
            if (accessDomain.entityTypeId == Constants.EntityTypes.ORG && accessDomain.entityId == orgId) {
                if (accessDomain.roleId == this.ORG_ADMIN_ROLE_ID) {
                    isUserAdminOfOrg = true;
                }
            }
        });
        return isUserAdminOfOrg;
    }

    isUserAdminOrBackUpAdminOfOrg(orgId: number): boolean {
        let isUserAdminOfOrg: boolean = false;
        let userAccessDomains = this.getLoggedInUserAccessDomains() as AccessDomain[];
        if (!userAccessDomains || userAccessDomains.length == 0) {
            return false;
        }
        userAccessDomains?.forEach((accessDomain: AccessDomain) => {
            if (accessDomain.entityTypeId === Constants.EntityTypes.ORG && accessDomain.entityId === orgId) {
                if (
                    accessDomain.roleId == this.ORG_ADMIN_ROLE_ID ||
                    accessDomain.roleId == this.BACKUP_ORG_ADMIN_ROLE_ID
                ) {
                    isUserAdminOfOrg = true;
                }
            }
        });
        return isUserAdminOfOrg;
    }

    filterUserTeamsFromAllTeams(allTeams: AllTeamsOfOrgResponse[]): AllTeamsOfOrgResponse[] {
        const userAccessDomains = this.getLoggedInUserAccessDomains() as AccessDomain[];
        allTeams = allTeams.filter((team: AllTeamsOfOrgResponse) => {
            return userAccessDomains.some((accessDomain: AccessDomain) => {
                return accessDomain.entityTypeId == Constants.EntityTypes.TEAM && accessDomain.entityId == team.teamId;
            });
        });
        return allTeams;
    }

    getTimeZone() {
        return Intl.DateTimeFormat().resolvedOptions().timeZone;
    }

    getPersonalOrgId() {
        return 0; // Personal orgId is 0
    }

    isUserRegisteredInPersonalOrganization(): boolean {
        const userAccounts = this.getUserAccounts();
        const isPersonalOrgPresent = userAccounts?.find((acc) => {
            return acc.orgId === Constants.EntityTypes.PERSONAL_ORG_ID;
        });
        return !!isPersonalOrgPresent;
    }

    isUserHasAdminAccessInAnyOrg(): boolean {
        let userAccessDomains = this.getLoggedInUserAccessDomains() as AccessDomain[];
        return userAccessDomains && userAccessDomains.length > 0
            ? userAccessDomains.some((accessDomain: AccessDomain) => {
                  return (
                      accessDomain.entityTypeId == Constants.EntityTypes.ORG &&
                      (accessDomain.roleId == this.ORG_ADMIN_ROLE_ID ||
                          accessDomain.roleId == this.BACKUP_ORG_ADMIN_ROLE_ID)
                  );
              })
            : false;
    }

    isUserHasAdminAccessInAnyProject(): boolean {
        const userAccessDomains = this.getLoggedInUserAccessDomains() as AccessDomain[];
        return userAccessDomains && userAccessDomains.length > 0
            ? userAccessDomains.some((accessDomain: AccessDomain) =>
                accessDomain.entityTypeId === Constants.EntityTypes.PROJECT &&
                Constants.PROJECT_ADMIN_LEVEL_ROLES.includes(accessDomain.roleId)
            )
            : false;
    }

    getUserAccountIdsFromToken() {
        let decodedToken = this.getDecodedJwtToken();
        return decodedToken?.acountIds || [];
    }

    areListsIdentical(list1: number[], list2: number[]): boolean {
        if (list1.length !== list2.length) {
            return false;
        }
        const frequencyMap = new Map<number, number>();
        // Count each number's frequency in the first list
        for (const num of list1) {
            if (frequencyMap.has(num)) {
                frequencyMap.set(num, frequencyMap.get(num)! + 1);
            } else {
                frequencyMap.set(num, 1);
            }
        }
        // Match the frequency with the second list
        for (const num of list2) {
            if (!frequencyMap.has(num) || frequencyMap.get(num) === 0) {
                return false;
            }
            frequencyMap.set(num, frequencyMap.get(num)! - 1);
        }
        return true;
    }

    storeUserCountryDetails(fkCountryId: Country) {
        this.secureStorageService.setItem(LocalStorageRelatedData.USER_COUNTRY_ID, fkCountryId.countryId);
    }

    getUserCountryId() {
        return this.secureStorageService.getItem<number>(LocalStorageRelatedData.USER_COUNTRY_ID);
    }

    isUserSuperAdmin(): boolean {
        const userAllAccessDomains = this.getLoggedInUserAccessDomains() as AccessDomain[];
        return userAllAccessDomains.some((accessDomain: AccessDomain) => {
            return accessDomain.roleId === Constants.Roles.SUPER_ADMIN_ROLE_ID;
        });
    }

    async selectUserFromFuzzyMatch(
        query: string,
        usersToSearch: UserDetails[],
        title: string
    ): Promise<number | null> {
        const userMatch = fuzzysort.go(query, usersToSearch, {
            keys: ['firstName', 'lastName', 'email'],
        });

        if (userMatch.length > 1) {
            const userList: UserOption[] = userMatch.map(user => {
                return {
                    accountId: user.obj.accountId,
                    firstName: user.obj.firstName,
                    lastName: user.obj.lastName,
                    email: user.obj.email,
                }
            })

            const dialogRef = this.dialog.open(UserOptionSelectComponent, {
                width: '400px',
                maxHeight: '500px',
                data: {
                    title,
                    options: userList,
                },
            });

            const result = await firstValueFrom(dialogRef.afterClosed());
            return result ? Number(result) : null;
        } else if (userMatch.length === 1) {
            return Number(userMatch[0].obj.accountId);
        } else {
            this.toastr.error('No Assignee Found.');
            return null;
        }
    }

    async selectExtendedLabelUserOptionFromFuzzyMatch(
        query: string,
        optionsToSearch: DropDownOption<any>[],
        title: string
    ): Promise<DropDownOption<any> | null> {

        const searchResult = fuzzysort.go(query, optionsToSearch, {
            keys: ['label'],
        });

        if (searchResult.length > 1) {
            const optionList = searchResult.map(item => item.obj);

            const dialogRef = this.dialog.open(UserExtendedLabelOptionSelectComponent, {
                width: '400px',
                maxHeight: '500px',
                data: {
                    title,
                    options: optionList,
                },
            });

            const result = await firstValueFrom(dialogRef.afterClosed());

            if (!result) return null;

            // Return entire option object, not just accountId
            return optionList.find(opt => opt.value?.accountId === result) ?? null;

        } else if (searchResult.length === 1) {
            return searchResult[0].obj;
        } else {
            this.toastr.error('No match found.');
            return null;
        }
    }
}
